import React, { useCallback, useMemo, useState } from 'react'

import { Suggestion } from '@components/common/Suggestion'
import { Button, ButtonClean, Toasts, useMediaQueryContext } from '@fourthwall/components'
import { useWebViewBridge } from '@fourthwall/webview-bridge'

import { useSetShopFlagMutation } from '../../../../../Home/queries/useSetShopFlagMutation'
import { useCurrentShop } from '../../../../../hooks/useCurrentShop'
import { type Recommendation } from '../../../Recommendations.types'
import { useRecommendationsDismiss } from '../../../hooks/useRecommendationsDismiss'
import { useReferralConfigurationQuery } from '../../../queries/useReferralConfigurationQuery'
import addRefLinkSection from '../assets/add-ref-link-section.png'
import { useCreateSectionMutation } from '../queries/useCreateSectionMutation'

const FLAG_NAME = 'theme-referral-section-added'

export const useAddReferralsToHomePageRecommendation = (): {
  addReferralLinkToHomepageRecommendation: Recommendation
} => {
  const media = useMediaQueryContext()
  const [isLoading, setIsLoading] = useState(false)
  const { methods } = useWebViewBridge()
  const { createOnDismissCallback, recommendationsDismiss } = useRecommendationsDismiss()
  const { currentShop, invalidateCurrentShopQuery } = useCurrentShop()
  const { createSectionMutation } = useCreateSectionMutation()
  const { setShopFlagMutation } = useSetShopFlagMutation()

  const { referralConfigurationQuery } = useReferralConfigurationQuery(
    [{ pathOverride: 'start' }],
    {
      onError: undefined,
    },
  )
  const isAffiliatePlusUser = Boolean(referralConfigurationQuery.data?.affiliateType)
  const description = isAffiliatePlusUser
    ? 'Earn up to $10K USD for anyone you refer. They get sample credit and their own custom Fourthwall storefront.'
    : 'Make more when you refer other creators to Fourthwall. They get sample credit and their own custom Fourthwall storefront.'

  const handleSectionCreate = useCallback(async () => {
    setIsLoading(true)

    if (!referralConfigurationQuery.data?.shortLink || !currentShop) {
      Toasts.notify('Could not add referral link to storefront. Please try again later.')
      setIsLoading(false)

      return
    }

    try {
      await createSectionMutation.mutateAsync([
        {
          shop_id: currentShop.id,
          theme_id: 'main',
        },
        {
          platformSection: 'content_for_index',
          shopThemeSectionType: 'links-list',
          shopThemeSectionName: 'Your Referral Link',
          shopThemeSectionSettings: {
            heading: 'Want to make your own custom shop?' as never,
            text: 'Use my link below to get free store credit and build your own custom storefront on Fourthwall.' as never,
            content_alignment: 'center' as never,
            style: 'horizontal' as never,
            padding_top: 48 as never,
            padding_bottom: 48 as never,
          },
          shopThemeSectionBlocks: [
            {
              type: 'button',
              settings: [
                {
                  inputType: 'text',
                  id: 'text',
                  value: 'Launch your shop',
                },
                {
                  inputType: 'url',
                  id: 'url',
                  value: referralConfigurationQuery.data.shortLink,
                },
              ],
            },
          ],
        },
      ])

      await setShopFlagMutation.mutateAsync([
        {
          flag: FLAG_NAME,
        },
      ])

      await invalidateCurrentShopQuery()

      Toasts.notify('Referral link added to your storefront.', {
        type: 'success',
      })
      await methods.showInfoBanner?.('Referral link added to your storefront.')
    } catch (error) {
      Toasts.notify('Could not add referral link to storefront. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }, [
    createSectionMutation,
    currentShop,
    invalidateCurrentShopQuery,
    methods,
    referralConfigurationQuery.data?.shortLink,
    setShopFlagMutation,
  ])

  const addReferralLinkToHomepageRecommendation = useMemo(() => {
    return {
      id: 'addReferralLinkToHomepage' as Recommendation['id'],
      enabled: currentShop?.metafields[FLAG_NAME] !== true,
      isLoading: false,
      suggestion: (
        <Suggestion
          title="Add your referral link to your storefront"
          description={
            <p>
              {description}{' '}
              <ButtonClean
                label="Terms & Conditions"
                href="https://fourthwall.com/blog/fourthwalls-affiliate-program#%F0%9F%8C%B1-general-referral"
              />
            </p>
          }
          imageUrl={addRefLinkSection}
          content={
            <Button
              fullWidth={!media.tablet}
              disabled={isLoading}
              size="small"
              label="Add referrals to homepage"
              analytics="addReferralLinkToHomepage"
              onClick={handleSectionCreate}
            />
          }
          onDismiss={createOnDismissCallback(
            recommendationsDismiss.addReferralLinkToHomepage.dismiss,
          )}
        />
      ),
    } satisfies Recommendation
  }, [
    createOnDismissCallback,
    description,
    handleSectionCreate,
    isLoading,
    media.tablet,
    recommendationsDismiss.addReferralLinkToHomepage.dismiss,
  ])

  return {
    addReferralLinkToHomepageRecommendation,
  }
}
